import React, { useState, useMemo } from 'react';
import NotificationList from '@/components/notifications/NotificationList';
import { usePersonnelNotifications } from './hooks/usePersonnelNotifications';
import { Bell, BellRing, ClipboardCheck, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import NotificationSearch from '@/components/notifications/NotificationSearch';

const PersonnelNotificationsPage = () => {
   const [searchQuery, setSearchQuery] = useState('');
   const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
   const [activeTab, setActiveTab] = useState('all');
   const {
      notifications,
      unreadNotifications,
      loading,
      markAsRead,
      markAllAsRead
   } = usePersonnelNotifications();

   // Filter notifications based on search query and date range
   const filterNotifications = (notifs) => {
      if (!notifs) return notifs;

      let filtered = notifs;

      // Apply search filter
      if (searchQuery) {
         const query = searchQuery.toLowerCase();
         filtered = filtered.filter(notification =>
            notification.message.toLowerCase().includes(query) ||
            notification.type.toLowerCase().includes(query)
         );
      }

      // Apply date filter
      if (dateRange.from || dateRange.to) {
         filtered = filtered.filter(notification => {
            const notificationDate = new Date(notification.createdAt);
            const isAfterStart = !dateRange.from || notificationDate >= dateRange.from;
            const isBeforeEnd = !dateRange.to || notificationDate <= new Date(dateRange.to.setHours(23, 59, 59, 999));
            return isAfterStart && isBeforeEnd;
         });
      }

      return filtered;
   };

   // Clear all filters
   const handleClearFilters = () => {
      setSearchQuery('');
      setDateRange({ from: undefined, to: undefined });
   };

   // Memoize filtered notifications
   const filteredNotifications = useMemo(() =>
      filterNotifications(notifications), [notifications, searchQuery, dateRange]
   );

   const filteredPendingReviewNotifications = useMemo(() =>
      filterNotifications(notifications?.filter(n =>
         n.type.includes('PENDING_') || n.type.includes('NEEDS_REVIEW')
      )), [notifications, searchQuery, dateRange]
   );

   const filteredActionRequiredNotifications = useMemo(() =>
      filterNotifications(notifications?.filter(n =>
         n.metadata?.actionRequired && !n.read
      )), [notifications, searchQuery, dateRange]
   );

   const filteredUnreadNotifications = useMemo(() =>
      filterNotifications(unreadNotifications), [unreadNotifications, searchQuery, dateRange]
   );

   const tabs = [
      {
         value: 'all',
         icon: <Bell className="h-4 w-4" />,
         label: 'All',
         content: filteredNotifications,
         loading: loading,
         hasMore: false,
         onLoadMore: () => { }
      },
      {
         value: 'unread',
         icon: <BellRing className="h-4 w-4" />,
         label: 'Unread',
         count: filteredUnreadNotifications?.length,
         content: filteredUnreadNotifications,
         loading: loading,
         hasMore: false,
         onLoadMore: () => { }
      },
      {
         value: 'pending',
         icon: <ClipboardCheck className="h-4 w-4" />,
         label: 'Pending Review',
         count: filteredPendingReviewNotifications?.length,
         content: filteredPendingReviewNotifications,
         loading: loading,
         hasMore: false,
         onLoadMore: () => { }
      },
      {
         value: 'action',
         icon: <AlertCircle className="h-4 w-4" />,
         label: 'Action Required',
         count: filteredActionRequiredNotifications?.length,
         content: filteredActionRequiredNotifications,
         loading: loading,
         hasMore: false,
         onLoadMore: () => { }
      }
   ];

   const activeTabData = tabs.find(tab => tab.value === activeTab);

   return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white pt-24 dark:from-gray-900 dark:to-gray-800">
         <div className="max-w-[1200px] h-[900px] mx-auto flex shadow-xl">
            {/* Sidebar */}
            <div className="w-64 bg-card border-r shrink-0">
               <div className="p-6">
                  <h2 className="font-semibold text-lg mb-4">Personnel Notifications</h2>
                  {unreadNotifications?.length > 0 && (
                     <button
                        onClick={markAllAsRead}
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                     >
                        Mark all as read
                     </button>
                  )}
               </div>

               {/* Category List */}
               <nav className="space-y-1 px-4">
                  {tabs.map(tab => (
                     <button
                        key={tab.value}
                        onClick={() => setActiveTab(tab.value)}
                        className={cn(
                           "w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-colors",
                           "hover:bg-accent hover:text-accent-foreground",
                           activeTab === tab.value ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                        )}
                     >
                        {tab.icon}
                        <span>{tab.label}</span>
                        {tab.count > 0 && (
                           <span className="ml-auto bg-primary/10 text-primary text-xs rounded-full px-2 py-0.5">
                              {tab.count}
                           </span>
                        )}
                     </button>
                  ))}
               </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 bg-background">
               {/* Search Bar */}
               <div className="border-b p-6">
                  <NotificationSearch
                     searchQuery={searchQuery}
                     onSearchChange={setSearchQuery}
                     dateRange={dateRange}
                     onDateChange={setDateRange}
                     onClearFilters={handleClearFilters}
                  />
               </div>

               {/* Notifications List */}
               <div className="flex-1 overflow-hidden">
                  <NotificationList
                     notifications={activeTabData.content}
                     loading={activeTabData.loading || loading}
                     onMarkAsRead={markAsRead}
                     unreadOnly={activeTab === 'unread'}
                     hasMore={activeTabData.hasMore}
                     onLoadMore={activeTabData.onLoadMore}
                     searchQuery={searchQuery}
                  />
               </div>
            </div>
         </div>
      </div>
   );
};

export default PersonnelNotificationsPage;
